import {
  faBooks,
  faChartLine,
  faDatabase,
  faFileCertificate,
  faGlobeAmericas,
  faMountains,
  faPlug,
  faSeedling,
  faShirt,
  faSign,
  faTable,
  faTachometer,
  faTools,
  faUserHardHat,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { fetchCompanyProfile } from '@/api/companies';
import { PermissionSetsProvider } from '@/app/(client)/company-settings/-components/permissionSets/PermissionSetsProvider';
import { CompanyDetailError } from '@/app/(client)/network/-components/CompanyDetailError';
import CompanyHeader from '@/app/(client)/network/-components/CompanyHeader';
import { useAmforiCredentials } from '@/app/(client)/network/-hooks/useAmforiCredentials';
import { useCompanyDetail } from '@/app/(client)/network/-hooks/useCompanyDetail';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/network/${params.companyId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/network/$companyId/general-info',
        params: { companyId: params.companyId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['network', 'company', params.companyId],
      queryFn: () => fetchCompanyProfile(params.companyId),
    }),
  errorComponent: CompanyDetailError,
  component: CompanyDetailLayout,
});

function isFeatureEnabled(
  features: Array<{ id: string }> | undefined,
  id: string,
): boolean {
  return features?.some(f => f.id === id) ?? false;
}

function CompanyDetailLayout() {
  const { companyId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'companyDetail' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { data: company } = useCompanyDetail(companyId);
  const user = useSessionStore(s => s.user);

  const enabledFeatures = user?.company?.enabledFeatures;
  const isOwnCompany = user?.companyId === (company?.forCompanyId ?? companyId);
  const base = `/network/${companyId}`;

  // Integrations tab gating mirrors v1, minus the caller's own company: that
  // case moved to Company Settings (`/company-settings/integrations`), so this
  // profile only ever shows it for a SUPPLIER profile the viewing brand has
  // saved amfori creds against.
  const { data: amforiCredentials } = useAmforiCredentials();
  const brandHasAmforiCreds = Boolean(
    amforiCredentials?.clientSecretSet && amforiCredentials?.memberAmforiId,
  );

  // The CAPA tab embeds the capa-v2 actions list scoped to this company, so it
  // is gated on `capa-v2` alone — the old CAPA flags don't grant that
  // experience. The sidebar's CAPA nav gate (lib/utils/sidebarNavHelpers.ts) is
  // intentionally broader and left unchanged.
  const isCapaV2Enabled = isFeatureEnabled(enabledFeatures, 'capa-v2');

  // Which tab is open, so the permission-sets store only fetches for the two
  // screens that read it.
  const pathname = useRouterState({ select: s => s.location.pathname });

  const tabs: TabEntry[] = [
    {
      key: 'general-info',
      label: t('tabs.generalInfo'),
      icon: <FontAwesomeIcon icon={faGlobeAmericas} />,
      to: `${base}/general-info`,
    },
    // Own-company team management now lives solely in Company Settings
    // (`/company-settings/users`) — this tab used to also render for the
    // caller's own company, but that duplicated the same screen behind two
    // paths. Kept for another company's profile, where it's a read-only
    // roster (`canManageAccess` requires `isOwnCompany` in `UsersTable`) and
    // has nowhere else to live. Right after General Information, second in
    // the row, since "who am I dealing with here" follows straight from
    // "what is this company".
    ...(!isOwnCompany
      ? [
          {
            key: 'users',
            label: t('tabs.users'),
            icon: <FontAwesomeIcon icon={faUsers} />,
            to: `${base}/users`,
          } as TabEntry,
        ]
      : []),
    {
      key: 'esg-info',
      label: t('tabs.esgInfo'),
      icon: <FontAwesomeIcon icon={faSeedling} />,
      to: `${base}/esg-info`,
    },
    ...(isFeatureEnabled(enabledFeatures, 'risk-analysis-v3') || isOwnCompany
      ? [
          {
            key: 'risk-profile',
            label: t('tabs.riskProfile'),
            icon: <FontAwesomeIcon icon={faMountains} />,
            to: `${base}/risk-profile`,
          } as TabEntry,
        ]
      : []),
    {
      key: 'claims',
      label: t('tabs.certifications'),
      icon: <FontAwesomeIcon icon={faFileCertificate} />,
      to: `${base}/claims`,
    },
    {
      key: 'audits',
      label: t('tabs.audits'),
      icon: <FontAwesomeIcon icon={faUserHardHat} />,
      to: `${base}/audits`,
    },
    {
      key: 'documents',
      label: t('tabs.documents'),
      icon: <FontAwesomeIcon icon={faBooks} />,
      to: `${base}/documents`,
    },
    ...(isCapaV2Enabled
      ? [
          {
            key: 'capa',
            label: t('tabs.capa'),
            icon: <FontAwesomeIcon icon={faTable} />,
            to: `${base}/capa`,
          } as TabEntry,
        ]
      : []),
    ...(!isOwnCompany
      ? [
          {
            key: 'products',
            label: t('tabs.products'),
            icon: <FontAwesomeIcon icon={faShirt} />,
            to: `${base}/products`,
          } as TabEntry,
        ]
      : []),
    // Own-company amfori setup moved to Company Settings
    // (`/company-settings/integrations`) — this tab is now only the
    // supplier's amfori link, shown to a brand that has saved credentials.
    ...(brandHasAmforiCreds
      ? [
          {
            key: 'integrations',
            label: t('tabs.integrations'),
            icon: <FontAwesomeIcon icon={faPlug} />,
            to: `${base}/integrations`,
          } as TabEntry,
        ]
      : []),
    ...(!isOwnCompany
      ? [
          {
            key: 'questionnaire',
            label: t('tabs.questionnaire'),
            icon: <FontAwesomeIcon icon={faTachometer} />,
            to: `${base}/questionnaire`,
          } as TabEntry,
        ]
      : []),
    ...(isFeatureEnabled(enabledFeatures, 'custom-information-fields') &&
    !isOwnCompany
      ? [
          {
            key: 'custom-information',
            label: t('tabs.customInformation'),
            icon: <FontAwesomeIcon icon={faDatabase} />,
            to: `${base}/custom-information`,
          } as TabEntry,
        ]
      : []),
    ...(isFeatureEnabled(enabledFeatures, 'custom-information-fields') &&
    isOwnCompany
      ? [
          {
            key: 'custom-information-fields',
            label: t('tabs.customInformationFields'),
            icon: <FontAwesomeIcon icon={faTools} />,
            to: `${base}/custom-information-fields`,
          } as TabEntry,
        ]
      : []),
    ...(!isOwnCompany
      ? [
          {
            key: 'webshop-profile',
            label: t('tabs.webshopProfile'),
            icon: <FontAwesomeIcon icon={faSign} />,
            to: `${base}/webshop-profile`,
          } as TabEntry,
          {
            key: 'slcp',
            label: t('tabs.slcp'),
            icon: <FontAwesomeIcon icon={faChartLine} />,
            to: `${base}/slcp`,
          } as TabEntry,
        ]
      : []),
  ];

  return (
    // Permission sets themselves moved to Company Settings — Users is the
    // only screen left here that reads the store (`SetChipsCell`'s badges,
    // `ManageAccessSheet`'s assign flow), and only for the caller's OWN
    // company: on another company's profile the tab is a read-only roster
    // (`canManageAccess` requires `isOwnCompany` in `UsersTable`), so there is
    // nothing there that needs the sets data. `enabled` keeps every other tab,
    // and every other company's profile, from paying for a fetch of a
    // tenant's model that isn't theirs to read.
    <PermissionSetsProvider
      companyId={companyId}
      enabled={isOwnCompany && pathname.startsWith(`${base}/users`)}
    >
      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
        <CompanyHeader
          company={company}
          companyId={companyId}
          isOwnCompany={isOwnCompany}
        />
        <div className='sticky top-0 z-20 bg-background'>
          <PageTabs tabs={tabs} mode='path' moreLabel={tShared('moreTabs')} />
        </div>
        {/* The floor makes short viewports scroll the page (header away, tabs
            sticky) instead of squeezing a table tab into the leftover under
            the tall header. No overflow-* on this wrapper: it would become
            the scrollport and unpin tab toolbars stuck against the root. */}
        <div className='flex flex-col flex-1 min-h-[70dvh]'>
          <Outlet />
        </div>
      </div>
    </PermissionSetsProvider>
  );
}
